import { renderIconToElement } from "@platform/design-system";
import { useDialogs } from "@platform/hooks";
import { useCallback } from "react";
import {
  createCommentContextMenuItems,
  isBenefitLabelColumn,
  parsePlanField,
  qualifyFieldPath,
} from "@platform/plan-grid-core";
import { useCellReviews } from "@platform/plan-grid-core";

import { useSbsCellState } from "../components/PlanEditor/context/SbsCellStateContext";
import { DISMISS_DIFFERENCE_LABEL } from "../utilities/differenceStyles";

import type { CommentDialogData, ReviewLike } from "@platform/plan-grid-core";
import type {
  DefaultMenuItem,
  GetContextMenuItemsParams,
  MenuItemDef,
} from "ag-grid-community";

type ContextMenuItemsGetter = (
  params: GetContextMenuItemsParams,
) => (DefaultMenuItem | MenuItemDef)[];

/**
 * Encapsulates SBS-specific context menu logic: broker highlights + review items.
 * Must be called inside CellReviewProvider, DialogProvider and SbsCellStateProvider
 * (the last one supplies the "Dismiss Different than Requested" action).
 *
 * Returns a getContextMenuItems callback suitable for PlanEditor's prop.
 */
export function useSbsContextMenu(): ContextMenuItemsGetter {
  const cellReviews = useCellReviews();
  const { getCellMeta, dismissDifference } = useSbsCellState();
  const { openDialog } = useDialogs();

  const getContextMenuItems = useCallback(
    (params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
      const field = params.column?.getColDef()?.field;
      const benefitKey = params.node?.data?.benefitKey as string | undefined;

      if (!field || !benefitKey) {
        return [];
      }

      if (isBenefitLabelColumn(field)) {
        return [];
      }

      const parsed = parsePlanField(field);
      if (!parsed) {
        return [];
      }

      const { planId, network } = parsed;
      // Qualified fieldPath mirrors what cellContextMenu.ts computes internally.
      const fieldPath = qualifyFieldPath(benefitKey, network);

      const isHighlighted = cellReviews.getBrokerHighlight(planId, fieldPath);

      const sbsItems: MenuItemDef[] = [
        {
          name: `${isHighlighted ? "Remove Highlight" : "Broker Highlight"}`,
          icon: renderIconToElement("highlight", "sm", "muted"),
          action: () => {
            if (isHighlighted) {
              cellReviews.clearBrokerHighlight(planId, fieldPath);
            } else {
              cellReviews.setBrokerHighlight(planId, fieldPath);
            }
          },
        },
      ];

      // Only a cell currently flagged different than requested has a flag to dismiss.
      const hasDifference =
        getCellMeta(benefitKey, planId, network)?.statuses.includes(
          "difference",
        ) ?? false;
      const differenceItems: (DefaultMenuItem | MenuItemDef)[] = hasDifference
        ? [
            "separator",
            {
              name: DISMISS_DIFFERENCE_LABEL,
              icon: renderIconToElement("syncProblem", "sm", "muted"),
              action: () => dismissDifference(benefitKey, planId, network),
            },
          ]
        : [];

      // Per-cell review callbacks. planId and fieldPath are captured in this closure.
      const reviewCallbacks = {
        getFieldComment: (pid: string, fp: string) =>
          cellReviews.getReview(pid, fp),
        onNeedsReview: (
          pid: string,
          fp: string,
          existingReview?: ReviewLike,
        ) => {
          const hasReview = Boolean(existingReview);
          const review = existingReview as
            | (ReviewLike & { createdAt?: string; updatedAt?: string })
            | undefined;
          const dialogData: CommentDialogData = {
            title: hasReview ? "Edit/View Review" : "Needs Review",
            currentComment: existingReview?.comment ?? "",
            planId: pid,
            fieldPath: fp,
            createdAt: review?.createdAt,
            updatedAt: review?.updatedAt,
            onSubmit: (comment: string) => {
              cellReviews.upsertReview(pid, fp, comment || null);
            },
          };
          openDialog<CommentDialogData>("comment", dialogData);
        },
        onClearReview: (_pid: string, _existingReview: ReviewLike) => {
          // Use planId and fieldPath from the closure — the same values that
          // cellContextMenu.ts derived, so the lookup is identical.
          cellReviews.clearReview(planId, fieldPath);
        },
      };

      const reviewItems = createCommentContextMenuItems(
        params,
        reviewCallbacks,
      );

      return [...sbsItems, ...reviewItems, ...differenceItems];
    },
    [cellReviews, getCellMeta, dismissDifference, openDialog],
  );

  return getContextMenuItems;
}
